import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import { useNavigate } from 'react-router-dom'
import type { OperatorListType, PlatformListType } from '../types'
import { ROUTES } from '../../../consts/Routes'
import { getAllOperatorsByInputAPI } from '../../../utils/api'
import CustomAutoComplete from '../../../components/CustomAutoComplete'
import CustomButton from '../../../components/CustomButton'
import CustomSnackBar from '../../../components/CustomSnackBar'
import { SnackBarMessageType } from '../../../consts/SnackbarMessageTypes'
import BotTypeDropDown from '../BotTypeDropDown'
import { useAppDispatch, useAppSelector } from '../../../reducers/hooks'
import { userReducer } from '../../../reducers/user/userReducer'

interface OperatorTabProps {
  operatorList: OperatorListType[]
  platformList: PlatformListType[]
}

const OperatorTab: React.FC<OperatorTabProps> = ({ operatorList, platformList }): JSX.Element => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [operators, setOperators] = useState(operatorList)
  const [selectedOperator, setSelectedOperator] = useState<OperatorListType | null>(null)
  const [selectedBotType, setSelectedBotType] = useState<string | null>(null)
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true)
  const [isLoadingOperators, setIsLoadingOperators] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const [debouncedInputValue, setDebouncedInputValue] = useState<string>('')
  const loadingMessage = 'Loading additional operators...'
  const platform = useAppSelector((state) => state.user.platform)
  const botType = useAppSelector((state) => state.user.botType)

  const handleInputChange = (event: any, newInputValue: string): void => {
    setInputValue(newInputValue)
  }

  const handleAutocompleteChange = (event: any, newValue: OperatorListType | null): void => {
    setSelectedOperator(newValue)
  }

  const handleButtonDisabled = (): void => {
    setIsButtonDisabled(!(selectedOperator !== null && selectedBotType !== null))
  }

  const handleButtonClick = (): void => {
    if (selectedOperator !== null) {
      dispatch(userReducer.actions.changePlatformId(selectedOperator.operator_platform_id))
      localStorage.setItem('platformId', selectedOperator.operator_platform_id)
      dispatch(userReducer.actions.changePlatformNickname(selectedOperator.operator))
      localStorage.setItem('platformNickname', selectedOperator.operator)
      navigate(ROUTES.CHAT)
    }
  }

  const fetchSelectedOperatorsFromAPI = async (inputValue: string): Promise<void> => {
    if (inputValue !== '' && selectedOperator === null) {
      setIsLoadingOperators(true)
      try {
        const response = await getAllOperatorsByInputAPI(inputValue, 50, platform, botType)
        if (response.payload !== null) {
          setIsLoadingOperators(false)
          setOperators(response?.payload)
        } else {
          setIsLoadingOperators(false)
          setOperators([])
        }
      } catch (error) {
        console.error('API Error:', error)
      }
    } else {
      setOperators(operatorList)
    }
  }

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedInputValue(inputValue)
    }, 1000)

    return () => {
      clearTimeout(debounceTimer)
    }
  }, [inputValue])

  useEffect(() => {
    if (debouncedInputValue !== '') {
      fetchSelectedOperatorsFromAPI(debouncedInputValue).catch((error) => {
        console.error('API Error:', error)
      })
    }
  }, [debouncedInputValue])

  useEffect(() => {
    handleButtonDisabled()
  }, [selectedOperator, selectedBotType])

  return (
    <>
      <Box width={'100%'} mb={2} mt={2}>
        <CustomAutoComplete
          options={operators}
          getOptionLabel={(operators) => `${operators.operator} ${operators.operator_platform_id}`}
          value={selectedOperator}
          onChange={handleAutocompleteChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          placeholder="Select Operator"
        />
      </Box>
      <BotTypeDropDown platformList={platformList} setSelectedBotType={setSelectedBotType}/>
      <CustomButton
        onClick={handleButtonClick}
        disabled={isButtonDisabled}
        label="Select Operator"
      />
      <CustomSnackBar isOpen={isLoadingOperators} onClose={setIsLoadingOperators} message={loadingMessage} duration={30000} messageType={SnackBarMessageType.INFO}/>
    </>
  )
}

export default OperatorTab
